<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import Button from "$app/components/ButtonSpinner.svelte"
  import Tags from "svelte-tags-input"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  interface Props {
    categories: string[]
    onAddedNewVideo: (video: LibraryVideoType) => void
  }
  let props = $props()
  let { categories, onAddedNewVideo }: Props = props

  let errors: any = $state({})

  const initialForm = $state({
    title: "",
    url: "",
    source: "youtube",
    thumbnail: null,
    categories: [],
    description: ""
  })

  let sourceTitle = () => {
    if (form.source === "youtube") {
      return "YouTube"
    } else if (form.source === "vimeo") {
      return "Vimeo"
    }
  }

  let thumbnailUrl = $state(null)
  let noDefaultThumbnail = $state(false)

  var getThumbnailUrl = async function (url) {
    thumbnail = null
    noDefaultThumbnail = false
    if (form.url === null || form.url.length <= 0) {
      thumbnailUrl = null
    }

    errors = {}

    if (form.url.toLowerCase().includes("youtube")) {
      form.source = "youtube"
    } else if (form.url.toLowerCase().includes("vimeo")) {
      form.source = "vimeo"
    } else {
      errors = { url: "Only valid YouTube or Vimeo links are allowed" }
      return false
    }

    if (form.source === "youtube") {
      let results = form.url.match("[\\?&]v=([^&#]*)")
      let video = results === null ? url : results[1]

      thumbnailUrl = "https://img.youtube.com/vi/" + video + "/hqdefault.jpg"
    } else if (form.source === "vimeo") {
      let results = form.url.match(
        "(?:https?://)?(?:www.)?vimeo.com/(?:(?:[a-z0-9]*/)*/?)?([0-9]+)"
      )
      let video = results === null ? url : results[1]

      thumbnailUrl = "https://vumbnail.com/" + video + "_large.jpg"
    }
  }

  let form = { ...initialForm }

  const fields = ["title", "url", "source", "categories", "description"]

  function getFormData(): FormData {
    const formData = new FormData()
    fields.forEach((field) => {
      if (["categories"].includes(field)) {
        form[field].forEach((value) => {
          formData.append(`library_video[${field}][]`, value)
        })
      } else {
        formData.append(`library_video[${field}]`, form[field] || "")
      }
    })

    if (thumbnail) {
      formData.append("library_video[thumbnail]", thumbnail)
    } else if (thumbnailUrl) {
      formData.append("library_video[thumbnail_source_url]", thumbnailUrl)
    }

    return formData
  }

  // Save New Video
  let savingVideo = $state(false)
  async function createVideo(): Promise<void> {
    savingVideo = true
    let newVideo = null

    errors = {}

    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/library.json`,
        getFormData()
      )
      newVideo = resp.data
      onAddedNewVideo(newVideo)
      toast("The video has been Added.", "success")
    } catch (e) {
      console.log(e)
      toast("There was an error saving the new video", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      savingVideo = false
    }
  }

  let thumbnail: any = $state(null)
  let thumbnailTmp: any = $state(null)
  let thumbnailInput: HTMLInputElement = $state(null)

  const onFileSelected = (e) => {
    thumbnail = e.target.files[0]

    let reader = new FileReader()
    reader.readAsDataURL(thumbnail)
    reader.onload = (e) => {
      thumbnailTmp = e.target.result
    }

    const imgThumbnail: HTMLImageElement =
      document.querySelector("#thumbnail-image")
    if (thumbnailTmp) {
      imgThumbnail.src = thumbnailTmp
    }
  }
</script>

<div class="mt-3 sm:mt-5 overflow-visible w-full">
  <div class="flex w-full justify-center items-center">
    <h3
      class="text-2xl lg:text-3xl text-center leading-6 font-medium text-gray-900"
    >
      Add a Video
    </h3>
  </div>

  <div class="rounded-md bg-blue-50 p-4 mt-4 mx-2 lg:mx-4">
    <div class="flex">
      <div class="flex-shrink-0">
        <svg
          class="h-5 w-5 text-blue-400"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <div class="ml-3 flex-1 md:flex md:justify-between">
        <p class="text-sm text-blue-700">
          Currently we support adding videos from either YouTube or Vimeo. The
          thumbnail image is automatically determined from the video, or you can
          override the default and upload your own.
        </p>
      </div>
    </div>
  </div>

  <div class="my-6 px-2 lg:px-4">
    <div class="grid grid-cols-1 gap-4">
      <div>
        <p class="mb-1">Title</p>
        <div class="input-cont" class:error={errors.title}>
          <input
            id="video_title"
            type="text"
            bind:value={form.title}
            class="input"
          />
          <div class="msg">{errors.title}</div>
        </div>
      </div>
      <div>
        <p class="mb-1">Source</p>
        <div class="input-cont" class:error={errors.source}>
          <select id="video_source" bind:value={form.source} class="input">
            <option value="youtube">YouTube</option>
            <option value="vimeo">Vimeo</option>
          </select>
          <div class="msg">{errors.source}</div>
        </div>
      </div>
      <div>
        <p class="mb-1">{sourceTitle()} Link</p>
        <div class="input-cont" class:error={errors.url}>
          <input
            id="video_url"
            type="text"
            bind:value={form.url}
            class="input"
            onblur={getThumbnailUrl}
          />
          <div class="msg">{errors.url}</div>
        </div>
      </div>
      <div>
        <p class="mb-1">Thumbnail Image</p>
        <div class="input-cont" class:error={errors.thumbnail}>
          {#if thumbnail}
            <img
              src={thumbnailTmp}
              id="thumbnail-image"
              alt="User avatar"
              class="w-auto max-h-48 bg-gray-300 shadow-lg flex-shrink-0"
            />
            <button
              class="relative cursor-pointer bg-white rounded-md font-medium text-black hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
              onclick={() => {
                thumbnail = null
              }}
            >
              {#if noDefaultThumbnail}
                <span>Replace Thumbnail Image</span>
              {:else}
                <span>Revert to Default Thumbnail</span>
              {/if}
            </button>
          {:else if thumbnailUrl}
            <img
              src={thumbnailUrl}
              id="thumbnail-image"
              alt="User avatar"
              class="w-auto max-h-48 bg-gray-300 shadow-lg flex-shrink-0"
            />
            <button
              class="relative cursor-pointer bg-white rounded-md font-medium text-black hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
              onclick={() => {
                thumbnailInput.click()
              }}
            >
              <span
                >Replace Default Thumbnail (Recommended size is 1280x720 or 16:9
                aspect ratio)</span
              >
            </button>
          {:else if noDefaultThumbnail}
            <div
              class="w-80 h-40 bg-gray-200 rounded flex items-center justify-center hover:cursor-pointer p-4"
            >
              <p class="text-xl text-gray-700 text-center">
                Unable to load default thumbnail
              </p>
            </div>
            <button
              class="relative cursor-pointer bg-white rounded-md font-medium text-black hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
              onclick={() => {
                thumbnailInput.click()
              }}
            >
              <span
                >Upload Thumbnail (Recommended size is 1280x720 or 16:9 aspect
                ratio)</span
              >
            </button>
          {:else}
            <div
              class="w-80 h-40 bg-gray-200 rounded flex items-center justify-center hover:cursor-pointer p-4"
            >
              <p class="text-xl text-gray-700 text-center">
                Enter Video Link For Thumbnail Image
              </p>
            </div>
          {/if}

          <input
            type="file"
            class="hidden"
            id="library_video_thumbnail"
            bind:this={thumbnailInput}
            required
            onchange={onFileSelected}
            accept=".jpg, .jpeg, .png"
          />
          <div class="msg">{errors.thumbnail}</div>
        </div>
      </div>

      <div>
        <p class="mb-1">Categories</p>
        <div class="input-cont" class:error={errors.categories}>
          <div class="svelte-tags">
            <Tags
              bind:tags={form.categories}
              autoComplete={categories}
              addKeys={[]}
              minChars={0}
              removeKeys={[8]}
              onlyUnique={true}
            />
          </div>
          <div class="msg">{errors.categories}</div>
        </div>
      </div>

      <div>
        <p class="mb-1">Description / Notes</p>
        <div class="input-cont" class:error={errors.description}>
          <textarea
            bind:value={form.description}
            rows="3"
            class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          ></textarea>
          <div class="msg">{errors.description}</div>
        </div>
      </div>

      <div class="flex items-center justify-center">
        <Button
          class="btn btn-rounded success btn-big"
          processing={savingVideo}
          onclick={() => {
            createVideo()
          }}
        >
          Add Video
        </Button>
      </div>
    </div>
  </div>
</div>
